/**
 * SettingsPage - 5-Section Information Architecture
 *
 * Reorganized per Frank's IA feedback (#803):
 * - ACCOUNT: Profile (identity & access)
 * - PREFERENCES: Notifications, Accessibility (how I want ORO to work)
 * - SUPPORT: FAQ, Contact (get help)
 * - ABOUT: About ORO move (story & credibility)
 * - LEGAL: Privacy, Terms (compliance only)
 * - Sign Out: Red text, standalone at bottom
 *
 * 3-Layer CSS Architecture (Nightfall Theme):
 * - Uses --oro-* semantic tokens (Layer 2) ONLY
 * - bg-oro-surface, text-text-primary, border-oro-border
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '../components';
import { PendingInvitesSection } from '../components/PendingInvitesSection';
import FeedbackModal from '../components/FeedbackModal';
import { useAuth } from '../hooks/useAuth';
import { useClubs } from '../hooks/useClubs';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import type { ClubWithRole } from '../lib/api/clubs';
import { User, Bell, Eye, Calendar, Ruler, Ban, ChevronRight, LogOut, MessageSquare, HelpCircle, PlayCircle, ShieldCheck, Plus, Users } from 'lucide-react';
import { BRAND_FULL } from '../constants/brand';
import { useTour } from '../components/tour/useTour';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { start: startTour } = useTour();
  const [showFeedback, setShowFeedback] = useState(false);

  // Set page title for browser history (#896)
  useDocumentTitle('Settings');

  // Clubs section (#2339) — quick access to existing clubs + the re-homed
  // Create a Club path (removed from the club switchers per ADR-UX-001).
  const { data: myClubs, error: clubsError, isLoading: clubsLoading } = useClubs();

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar
        title="Settings"
        onBack={() => navigate(-1)}
      />

      <div className="flex-1 overflow-y-auto pt-14">
        <div className="max-w-2xl mx-auto p-4 space-y-8">

          {/* Pending Invites - Shows when user has pending invites (#907) */}
          <PendingInvitesSection />

          {/* ACCOUNT Section - Identity & access (#803) */}
          <section>
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3 px-1">
              Account
            </h2>
            <div className="space-y-3">
              <SettingsCard
                icon={<User className="w-5 h-5" />}
                label="Profile"
                onClick={() => navigate('/settings/profile')}
              />
              {/* #531/#532 — GDPR self-service: data export + account deletion */}
              <SettingsCard
                icon={<ShieldCheck className="w-5 h-5" />}
                label="Account & Privacy"
                onClick={() => navigate('/settings/account')}
              />
            </div>
          </section>

          {/* Separator */}
          <div className="border-t border-oro-border" />

          {/* CLUBS Section - Manage clubs + create (#2339) */}
          <section>
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3 px-1">
              Clubs
            </h2>
            <div className="space-y-3">
              {/* Data wins over a background-refetch error: TanStack v5 keeps
                  cached data when a refetch fails, and error+data can coexist.
                  Only a data-less failure shows the error line. */}
              {myClubs ? (
                myClubs.map((club) => (
                  <ClubCard
                    key={club.id}
                    club={club}
                    onClick={() =>
                      // Settings hub is owner-only; everyone else lands on the club page.
                      navigate(club.is_owner ? `/clubs/${club.slug}/settings` : `/clubs/${club.slug}`)
                    }
                  />
                ))
              ) : clubsLoading ? (
                <div className="text-sm text-text-muted px-1">Loading clubs...</div>
              ) : clubsError ? (
                <div className="text-sm text-text-muted px-1">Unable to load your clubs.</div>
              ) : null}
              <SettingsCard
                icon={<Plus className="w-5 h-5" />}
                label="Create a Club"
                onClick={() => navigate('/clubs/create')}
              />
            </div>
          </section>

          {/* Separator */}
          <div className="border-t border-oro-border" />

          {/* PREFERENCES Section - How I want ORO to work (#803) */}
          <section data-tour="settings-preferences">
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3 px-1">
              Preferences
            </h2>
            <div className="space-y-3">
              <SettingsCard
                icon={<Bell className="w-5 h-5" />}
                label="Notifications"
                onClick={() => navigate('/settings/notifications')}
              />
              <SettingsCard
                icon={<Eye className="w-5 h-5" />}
                label="Accessibility"
                onClick={() => navigate('/settings/accessibility')}
              />
              <SettingsCard
                icon={<Calendar className="w-5 h-5" />}
                label="Calendar Feed"
                onClick={() => navigate('/settings/calendar')}
              />
              <SettingsCard
                icon={<Ruler className="w-5 h-5" />}
                label="Units & Display"
                onClick={() => navigate('/settings/preferences')}
              />
              {/* #3274 — always shown (was gated on having ≥1 block); the
                  destination renders an empty state when the list is empty. */}
              <SettingsCard
                icon={<Ban className="w-5 h-5" />}
                label="Blocked Users"
                onClick={() => navigate('/settings/blocked')}
              />
            </div>
          </section>

          {/* Separator */}
          <div className="border-t border-oro-border" />

          {/* SUPPORT Section - Get help (#803) */}
          <section>
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3 px-1">
              Support
            </h2>
            <div className="space-y-3">
              {/* Help Center - Card style to match Share Feedback */}
              <button
                onClick={() => navigate('/help')}
                className="w-full flex items-center gap-4 p-4 bg-oro-surface border border-oro-border rounded-xl hover:bg-oro-surface-alt transition-colors"
              >
                <div className="text-accent">
                  <HelpCircle className="w-5 h-5" />
                </div>
                <span className="flex-1 text-left text-text-primary font-medium">
                  Help Center
                </span>
                <ChevronRight className="w-5 h-5 text-text-muted" />
              </button>
              {/* Share Feedback - Card style for prominence (#1075) */}
              <button
                onClick={() => setShowFeedback(true)}
                className="w-full flex items-center gap-4 p-4 bg-oro-surface border border-oro-border rounded-xl hover:bg-oro-surface-alt transition-colors"
              >
                <div className="text-accent">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <span className="flex-1 text-left text-text-primary font-medium">
                  Share Feedback
                </span>
                <ChevronRight className="w-5 h-5 text-text-muted" />
              </button>
              {/* Replay Tour (#1559) */}
              <button
                onClick={startTour}
                className="w-full flex items-center gap-4 p-4 bg-oro-surface border border-oro-border rounded-xl hover:bg-oro-surface-alt transition-colors"
              >
                <div className="text-accent">
                  <PlayCircle className="w-5 h-5" />
                </div>
                <span className="flex-1 text-left text-text-primary font-medium">
                  Replay Tour
                </span>
                <ChevronRight className="w-5 h-5 text-text-muted" />
              </button>
            </div>
            <div className="space-y-2 mt-3">
              <SettingsTextLink
                label="FAQ"
                onClick={() => navigate('/faq')}
              />
              <SettingsTextLink
                label="Contact Us"
                onClick={() => navigate('/contact')}
              />
            </div>
          </section>

          {/* Separator */}
          <div className="border-t border-oro-border" />

          {/* ABOUT Section - Story & credibility (#803) */}
          <section>
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3 px-1">
              About
            </h2>
            <div className="space-y-2">
              <SettingsTextLink
                label={`About ${BRAND_FULL}`}
                onClick={() => navigate('/about')}
              />
              <SettingsTextLink
                label="Release Notes"
                onClick={() => navigate('/release-notes')}
              />
            </div>
          </section>

          {/* Separator */}
          <div className="border-t border-oro-border" />

          {/* LEGAL Section - Compliance only (#803) */}
          <section>
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3 px-1">
              Legal
            </h2>
            <div className="space-y-2">
              <SettingsTextLink
                label="Privacy Policy"
                onClick={() => navigate('/privacy')}
              />
              <SettingsTextLink
                label="Terms of Service"
                onClick={() => navigate('/terms')}
              />
            </div>
          </section>

          {/* Sign Out - Red text, standalone, generous margin */}
          <section className="pt-8">
            <button
              onClick={logout}
              className="w-full py-3 text-center text-oro-error hover:text-oro-error/80 transition-colors"
            >
              <span className="flex items-center justify-center gap-2">
                <LogOut className="w-4 h-4" />
                Sign Out
              </span>
            </button>
          </section>

          {/* Version Info - Reduced opacity (#747), dynamic values (#868, #882) */}
          <section className="pt-6 border-t border-oro-border mt-8">
            <dl className="space-y-2 text-sm opacity-50">
              <div className="flex justify-between">
                <dt className="text-text-muted">Release</dt>
                <dd className="text-text-muted font-mono">
                  {import.meta.env.VITE_APP_VERSION || 'v1.0.0'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-text-muted">Build</dt>
                <dd className="text-text-muted font-mono">
                  {import.meta.env.VITE_BUILD_NUMBER ? `#${import.meta.env.VITE_BUILD_NUMBER}` : 'local'}
                </dd>
              </div>
            </dl>
          </section>

        </div>
      </div>

      {/* Feedback Modal (#1075) */}
      <FeedbackModal
        isOpen={showFeedback}
        onClose={() => setShowFeedback(false)}
      />
    </div>
  );
}

/**
 * ClubCard - Clubs section row (#2339)
 * Avatar + name + role, card styling matching SettingsCard
 */
function ClubCard({ club, onClick }: { club: ClubWithRole; onClick: () => void }) {
  // is_owner, not role: creators carry role 'leader' (#2176 trap).
  const roleLabel = club.is_owner
    ? 'Owner'
    : club.role.charAt(0).toUpperCase() + club.role.slice(1);
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 p-4 bg-oro-surface border border-oro-border rounded-xl hover:bg-oro-surface-alt transition-colors"
    >
      {club.avatar_image_url ? (
        <img
          src={club.avatar_image_url}
          alt=""
          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
          loading="lazy"
          width={40}
          height={40}
        />
      ) : (
        <div className="w-10 h-10 rounded-full bg-oro-surface-alt flex items-center justify-center flex-shrink-0">
          <Users className="w-5 h-5 text-text-muted" />
        </div>
      )}
      <div className="flex-1 min-w-0 text-left">
        <div className="font-medium text-text-primary truncate">{club.name}</div>
        <div className="text-sm text-text-secondary">{roleLabel}</div>
      </div>
      <ChevronRight className="w-5 h-5 text-text-muted flex-shrink-0" />
    </button>
  );
}

/**
 * SettingsCard - Tier 1 (Account section)
 * Cards with gold icons, elevated styling
 */
function SettingsCard({
  icon,
  label,
  onClick
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 p-4 bg-oro-surface border border-oro-border rounded-xl hover:bg-oro-surface-alt transition-colors"
    >
      <div className="text-accent">
        {icon}
      </div>
      <span className="flex-1 text-left text-text-primary font-medium">
        {label}
      </span>
      <ChevronRight className="w-5 h-5 text-text-muted" />
    </button>
  );
}

/**
 * SettingsTextLink - Plain text links (#747)
 * No container/background, just text with hover color change
 */
function SettingsTextLink({
  label,
  onClick
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="block w-full text-left text-text-secondary hover:text-text-primary transition-colors py-2 px-1"
    >
      {label}
    </button>
  );
}
